/**
 * Local Storage Service for Manage Leads Page
 * Stores and retrieves user's last filter selections and selected lead
 */

import type {
  ContactInfoType,
  LeadStatusType,
  TechnologyType,
} from '@services/firebase/firestore';
import { getFromStorage, setInStorage } from './localStorage';

// Storage key
const STORAGE_KEY = 'leads';

/**
 * Schema version — bump this whenever the stored shape changes in a way that
 * requires migration.  On a version mismatch the stored data is migrated
 * (or wiped) and the new version is written back so the migration runs once.
 *
 * History:
 *   v1 — original schema (filter values stored as string | null)
 *   v2 — v0.8.0 multi-select (filter values stored as string[])
 *   v3 — v0.8.6 unified Types filter (adds `list: string[]` alongside businessType)
 */
const STORAGE_VERSION = 3;
const VERSION_KEY = 'manageLeads_version';

// Types
export interface ManageLeadsFilters {
  leadStatus: LeadStatusType[];
  businessType: string[];
  list: string[]; // Apollo list IDs (SC-2171, v0.8.6)
  city: string[];
  technology: TechnologyType[];
  contactInfo: ContactInfoType[];
  siteFilterText: string;
}

export interface ManageLeadsState {
  filters: ManageLeadsFilters;
  selected_lead: string | null;
}

// Default values
const DEFAULT_STATE: ManageLeadsState = {
  filters: {
    leadStatus: [],
    businessType: [],
    list: [],
    city: [],
    technology: [],
    contactInfo: [],
    siteFilterText: '',
  },
  selected_lead: null,
};

/** Converts a legacy single-select value (string | null) to an array */
function toArray(val: unknown): string[] {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val) return [val];
  return [];
}

/**
 * Migrates legacy single-select filter values (string | null) to arrays.
 * Pure function — callers are responsible for persisting the result.
 */
function migrateState(state: ManageLeadsState): ManageLeadsState {
  const f = state.filters;

  return {
    ...state,
    filters: {
      leadStatus: toArray(f.leadStatus) as LeadStatusType[],
      businessType: toArray(f.businessType),
      list: toArray((f as unknown as { list?: unknown }).list),
      city: toArray(f.city),
      technology: toArray(f.technology) as TechnologyType[],
      contactInfo: toArray(f.contactInfo) as ContactInfoType[],
      siteFilterText: f.siteFilterText ?? '',
    },
  };
}

/**
 * Gets the complete saved state from localStorage.
 * When the stored schema version is older than STORAGE_VERSION, the data is
 * migrated to the current schema and immediately persisted with the new version
 * so the migration only runs once.
 *
 * @returns Current state (migrated if necessary) or defaults
 */
export function getManageLeadsState(): ManageLeadsState {
  const storedVersion = parseInt(localStorage.getItem(VERSION_KEY) ?? '0', 10);
  const raw = getFromStorage<ManageLeadsState>(STORAGE_KEY, DEFAULT_STATE);

  if (storedVersion < STORAGE_VERSION) {
    // Schema is out of date — migrate, persist the result, and stamp the version
    const migrated = migrateState(raw);
    saveManageLeadsState(migrated);
    return migrated;
  }

  return raw;
}

/**
 * Saves the complete state to localStorage and stamps the current schema version.
 * @param state - The state to save
 */
export function saveManageLeadsState(state: ManageLeadsState): void {
  setInStorage(STORAGE_KEY, state);
  localStorage.setItem(VERSION_KEY, String(STORAGE_VERSION));
}

/**
 * Updates only the filters in the saved state
 * @param filters - The filter values to save
 */
export function updateFilters(filters: ManageLeadsFilters): void {
  const currentState = getManageLeadsState();
  saveManageLeadsState({
    ...currentState,
    filters,
  });
}

/**
 * Updates only the selected lead in the saved state
 * @param selectedLeadId - The lead ID to save
 */
export function updateSelectedLead(selectedLeadId: string | null): void {
  const currentState = getManageLeadsState();
  saveManageLeadsState({
    ...currentState,
    selected_lead: selectedLeadId,
  });
}

/**
 * Clears all saved state (resets to defaults)
 */
export function clearManageLeadsState(): void {
  saveManageLeadsState(DEFAULT_STATE);
}
